
import { useNavigate } from 'react-router-dom';

interface StatsModalProps {
  isOpen: boolean;
  onClose: () => void;
  type: 'market-cap' | 'volume' | 'holders' | 'staking';
}

export default function StatsModal({ isOpen, onClose, type }: StatsModalProps) {
  const navigate = useNavigate();

  if (!isOpen) return null;

  const statsData = {
    'market-cap': {
      title: 'Market Capitalization',
      value: '$2.4B',
      change: '+12.5%',
      description: 'Total market value of all PepCoin tokens in circulation',
      metrics: [
        { label: 'Circulating Supply', value: '850M PEP' },
        { label: 'Total Supply', value: '1B PEP' },
        { label: 'Price per Token', value: '$2.82' },
        { label: '24h Change', value: '+12.5%' }
      ],
      actions: [
        { label: 'Buy PepCoin', action: () => navigate('/pepcoin/buy-sell'), icon: 'ri-shopping-cart-line' },
        { label: 'View Trading', action: () => navigate('/trading'), icon: 'ri-line-chart-line' }
      ]
    },
    'volume': {
      title: '24h Trading Volume',
      value: '$156.8M',
      change: '+8.2%',
      description: 'Total trading volume across all exchanges in the last 24 hours',
      metrics: [
        { label: 'Spot Volume', value: '$142.3M' },
        { label: 'Futures Volume', value: '$14.5M' },
        { label: 'Top Exchange', value: 'PepCoin Pro' },
        { label: 'Volume Change', value: '+8.2%' }
      ],
      actions: [
        { label: 'Start Trading', action: () => navigate('/trading'), icon: 'ri-exchange-line' },
        { label: 'View Markets', action: () => navigate('/trading'), icon: 'ri-bar-chart-line' }
      ]
    },
    'holders': {
      title: 'Active Token Holders',
      value: '45.2K',
      change: '+15.3%',
      description: 'Number of unique wallet addresses holding PepCoin tokens',
      metrics: [
        { label: 'New Holders (24h)', value: '+1,247' },
        { label: 'Whale Holders', value: '892' },
        { label: 'Retail Holders', value: '44.3K' },
        { label: 'Growth Rate', value: '+15.3%' }
      ],
      actions: [
        { label: 'Create Wallet', action: () => navigate('/wallet'), icon: 'ri-wallet-3-line' },
        { label: 'Join Community', action: () => navigate('/pepverse'), icon: 'ri-group-line' }
      ]
    },
    'staking': {
      title: 'Staking Participation',
      value: '85%',
      change: 'Live',
      description: 'Percentage of total supply currently staked for rewards',
      metrics: [
        { label: 'Staked Amount', value: '722.5M PEP' },
        { label: 'Annual Yield', value: '12.5%' },
        { label: 'Active Stakers', value: '28.7K' },
        { label: 'Avg Stake Period', value: '6 months' }
      ],
      actions: [
        { label: 'Start Staking', action: () => navigate('/robot'), icon: 'ri-coins-line' },
        { label: 'View Rewards', action: () => navigate('/investment'), icon: 'ri-trophy-line' }
      ]
    }
  };

  const data = statsData[type];

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white/10 backdrop-blur-xl border border-white/20 rounded-3xl max-w-lg w-full p-8 text-white">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-2xl font-bold mb-1">{data.title}</h3>
            <div className="flex items-center gap-3">
              <span className="text-3xl font-black">{data.value}</span>
              <span className={`px-2 py-1 rounded-full text-sm font-bold ${
                data.change === 'Live' 
                  ? 'bg-pink-500/20 text-pink-400' 
                  : 'bg-green-500/20 text-green-400'
              }`}>
                {data.change}
              </span>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-10 h-10 bg-white/20 hover:bg-white/30 rounded-full flex items-center justify-center transition-colors duration-200"
          >
            <i className="ri-close-line text-xl"></i>
          </button>
        </div>
        
        <p className="text-gray-300 mb-6 leading-relaxed">{data.description}</p>
        
        <div className="space-y-3 mb-8">
          {data.metrics.map((metric, idx) => (
            <div key={idx} className="flex justify-between items-center p-3 bg-white/5 rounded-xl border border-white/10">
              <span className="text-gray-300">{metric.label}</span>
              <span className="font-bold text-lg">{metric.value}</span>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 gap-3">
          {data.actions.map((action, idx) => (
            <button
              key={idx}
              onClick={action.action}
              className="flex items-center justify-center gap-3 p-4 bg-gradient-to-r from-purple-500/20 to-cyan-500/20 hover:from-purple-500/30 hover:to-cyan-500/30 border border-purple-500/30 rounded-xl transition-all duration-300 hover:scale-105"
            >
              <i className={`${action.icon} text-xl`}></i>
              <span className="font-semibold">{action.label}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
